import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  ReactNode,
} from 'react';
import type {
  CardNote,
  CardNotesData,
  CardStatus,
  NoteEditMode,
} from '../types/CardNotes';
import {
  clearAllNotes as clearAllNotesApi,
  clearFieldNotes as clearFieldNotesApi,
  createNote as createNoteApi,
  deleteNote as deleteNoteApi,
  getCard as getCardApi,
  listCards,
  updateCard as updateCardApi,
  updateCardCode,
  updateNote as updateNoteApi,
  type ApiCard,
  type ApiNote,
  upsertManualCard,
  type UpsertManualCardPayload,
} from '../services/reviewFeedbackApi';
import { logError, logWarn } from '../utils/logger';

interface CardNotesContextType {
  notesData: CardNotesData;
  editMode: NoteEditMode | null;
  addNote: (
    cardId: string,
    field: string | undefined,
    noteText: string,
    originalValue?: unknown,
    editedValue?: unknown
  ) => Promise<void>;
  editNote: (noteId: string, noteText: string) => Promise<void>;
  deleteNote: (noteId: string) => Promise<void>;
  setEditMode: (cardId: string, field?: string) => void;
  clearEditMode: () => void;
  getCardNotes: (cardId: string, field?: string) => CardNote[];
  getFieldEditedValue: (cardId: string, field: string) => unknown | null;
  setFieldEditedValue: (
    cardId: string,
    field: string,
    value: unknown
  ) => Promise<void>;
  resetFieldEdits: (cardId: string, field?: string) => Promise<void>;
  saveAllChanges: () => void;
  discardAllChanges: () => void;
  hasUnsavedChanges: (cardId: string) => boolean;
  markCardAsCompleted: (cardId: string) => Promise<void>;
  markCardAsIncomplete: (cardId: string) => Promise<void>;
  isCardCompleted: (cardId: string) => boolean;
  markCardForRefetch: (cardId: string) => Promise<void>;
  markCardAsRefetched: (cardId: string) => Promise<void>;
  shouldCardRefetch: (cardId: string) => boolean;
  getCardCode: (
    cardId: string
  ) => { overrideCode: string | null; reviewBlurb: string | null; updatedAt?: Date };
  saveCardCode: (
    cardId: string,
    overrideCode: string | null,
    reviewBlurb: string | null
  ) => Promise<void>;
  reloadCard: (cardId: string) => Promise<void>;
  upsertManualCard: (
    cardId: string,
    payload: UpsertManualCardPayload
  ) => Promise<ApiCard>;
  archiveCard: (cardId: string) => Promise<void>;
  markCardAsDeleted: (cardId: string) => Promise<void>;
  restoreCard: (cardId: string) => Promise<void>;
  isCardArchived: (cardId: string) => boolean;
}

const CardNotesContext = createContext<CardNotesContextType | undefined>(
  undefined
);

type CardNotesEntry = CardNotesData[string];

const mapApiNoteToCardNote = (note: ApiNote): CardNote => ({
  id: note.id,
  cardId: note.cardId,
  field: note.field ?? undefined,
  noteText: note.noteText,
  originalValue: note.originalValue === null ? undefined : note.originalValue,
  editedValue: note.editedValue === null ? undefined : note.editedValue,
  createdAt: new Date(note.createdAt),
  updatedAt: new Date(note.updatedAt),
});

const mapApiCardToEntry = (card: ApiCard): CardNotesEntry => {
  const entry: CardNotesEntry = {
    generalNotes: card.generalNotes.map(mapApiNoteToCardNote),
    fieldNotes: {},
    status: card.status,
    visibility: card.visibility,
    shouldRefetch: card.shouldRefetch,
    isCompleted: card.status === 'completed',
    refetchAt: card.refetchAt ? new Date(card.refetchAt) : undefined,
    completedAt: card.completedAt ? new Date(card.completedAt) : undefined,
    createdAt: new Date(card.createdAt),
    updatedAt: new Date(card.updatedAt),
    codeOverride: card.overrideCode,
    codeReview: card.overrideReview,
    codeUpdatedAt: card.codeUpdatedAt ? new Date(card.codeUpdatedAt) : undefined,
    source: card.source,
    manualName: card.manualName ?? null,
    manualBank: card.manualBank ?? null,
    manualLinks: card.manualLinks ?? [],
    manualContext: card.manualContext,
    manualStatus: card.manualStatus ?? null,
    manualError: card.manualError ?? null,
    manualUpdatedAt: card.manualUpdatedAt
      ? new Date(card.manualUpdatedAt)
      : undefined,
    lastManualRunAt: card.lastManualRunAt
      ? new Date(card.lastManualRunAt)
      : undefined,
    lastFetchedAt: card.lastFetchedAt
      ? new Date(card.lastFetchedAt)
      : card.lastManualRunAt
      ? new Date(card.lastManualRunAt)
      : undefined,
    lastReviewedAt: card.lastReviewedAt
      ? new Date(card.lastReviewedAt)
      : new Date(card.updatedAt),
    hasUnsavedChanges: false,
  };

  Object.entries(card.fieldNotes).forEach(([field, notes]) => {
    entry.fieldNotes[field] = notes.map(mapApiNoteToCardNote);
  });

  return entry;
};

const findCardIdByNoteId = (
  notesData: CardNotesData,
  noteId: string
): string | null => {
  for (const [cardId, entry] of Object.entries(notesData)) {
    if (entry.generalNotes.some((note) => note.id === noteId)) {
      return cardId;
    }

    for (const notes of Object.values(entry.fieldNotes)) {
      if (notes.some((note) => note.id === noteId)) {
        return cardId;
      }
    }
  }

  return null;
};

export const CardNotesProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [notesData, setNotesData] = useState<CardNotesData>({});
  const [editMode, setEditModeState] = useState<NoteEditMode | null>(null);
  const notesDataRef = useRef<CardNotesData>({});

  const updateNotesData = useCallback(
    (updater: (previous: CardNotesData) => CardNotesData) => {
      setNotesData((prev) => {
        const next = updater(prev);
        notesDataRef.current = next;
        return next;
      });
    },
    []
  );

  useEffect(() => {
    notesDataRef.current = notesData;
  }, [notesData]);

  useEffect(() => {
    let isMounted = true;

    const loadNotes = async () => {
      try {
        const cards = await listCards();
        if (!isMounted) {
          return;
        }

        const initialData: CardNotesData = {};
        cards.forEach((card) => {
          initialData[card.cardId] = mapApiCardToEntry(card);
        });

        updateNotesData(() => initialData);
      } catch (error) {
        logError('Failed to load notes data from API:', error);
        if (!isMounted) {
          return;
        }
        // Initialize with empty data if API fails
        updateNotesData(() => ({}));
      }
    };

    loadNotes();

    return () => {
      isMounted = false;
    };
  }, [updateNotesData]);

  const applyCardResponse = useCallback(
    (card: ApiCard) => {
      updateNotesData((prev) => ({
        ...prev,
        [card.cardId]: mapApiCardToEntry(card),
      }));
    },
    [updateNotesData]
  );

  const refreshCard = useCallback(
    async (cardId: string) => {
      const card = await getCardApi(cardId);
      applyCardResponse(card);
    },
    [applyCardResponse]
  );

  const addNote = useCallback(
    async (
      cardId: string,
      field: string | undefined,
      noteText: string,
      originalValue?: unknown,
      editedValue?: unknown
    ) => {
      try {
        await createNoteApi(cardId, {
          field: field ?? null,
          noteText,
          originalValue,
          editedValue,
        });
        await refreshCard(cardId);
      } catch (error) {
        logError('Failed to create note:', error);
        throw error;
      }
    },
    [refreshCard]
  );

  const editNote = useCallback(
    async (noteId: string, noteText: string) => {
      const cardId = findCardIdByNoteId(notesDataRef.current, noteId);
      if (!cardId) {
        logWarn('Cannot locate card for note', noteId);
        return;
      }

      try {
        await updateNoteApi(noteId, { noteText });
        await refreshCard(cardId);
      } catch (error) {
        logError('Failed to update note:', error);
        throw error;
      }
    },
    [refreshCard]
  );

  const deleteNote = useCallback(
    async (noteId: string) => {
      const cardId = findCardIdByNoteId(notesDataRef.current, noteId);
      if (!cardId) {
        logWarn('Cannot locate card for note', noteId);
        return;
      }

      try {
        await deleteNoteApi(noteId);
        await refreshCard(cardId);
      } catch (error) {
        logError('Failed to delete note:', error);
        throw error;
      }
    },
    [refreshCard]
  );

  const setEditMode = useCallback((cardId: string, field?: string) => {
    setEditModeState({ cardId, field, isEditing: true });
  }, []);

  const clearEditMode = useCallback(() => {
    setEditModeState(null);
  }, []);

  const getCardNotes = useCallback(
    (cardId: string, field?: string): CardNote[] => {
      const entry = notesData[cardId];
      if (!entry) {
        return [];
      }

      if (field) {
        return entry.fieldNotes[field] || [];
      }

      return entry.generalNotes || [];
    },
    [notesData]
  );

  const getFieldEditedValue = useCallback(
    (cardId: string, field: string): unknown | null => {
      const entry = notesData[cardId];
      if (!entry) {
        return null;
      }

      const notes = entry.fieldNotes[field];
      if (!notes || notes.length === 0) {
        return null;
      }

      const latestNote = notes[notes.length - 1];
      return latestNote.editedValue ?? null;
    },
    [notesData]
  );

  const setFieldEditedValue = useCallback(
    async (cardId: string, field: string, value: unknown) => {
      const entry = notesDataRef.current[cardId];
      if (!entry) {
        return;
      }

      const fieldNotes = entry.fieldNotes[field];
      if (!fieldNotes || fieldNotes.length === 0) {
        return;
      }

      const latestNote = fieldNotes[fieldNotes.length - 1];

      try {
        await updateNoteApi(latestNote.id, { editedValue: value });
        await refreshCard(cardId);
      } catch (error) {
        logError('Failed to update edited value:', error);
        throw error;
      }
    },
    [refreshCard]
  );

  const resetFieldEdits = useCallback(
    async (cardId: string, field?: string) => {
      try {
        if (field) {
          await clearFieldNotesApi(cardId, field);
        } else {
          await clearAllNotesApi(cardId);
        }
        await refreshCard(cardId);
      } catch (error) {
        logError('Failed to reset field edits:', error);
        throw error;
      }
    },
    [refreshCard]
  );

  const saveAllChanges = useCallback(() => {
    // No-op: changes persist immediately through the API.
  }, []);

  const discardAllChanges = useCallback(() => {
    // No-op: changes persist immediately through the API.
  }, []);

  const hasUnsavedChanges = useCallback(
    (cardId: string): boolean => {
      return notesData[cardId]?.hasUnsavedChanges || false;
    },
    [notesData]
  );

  const markCardAsCompleted = useCallback(
    async (cardId: string) => {
      try {
        const card = await updateCardApi(cardId, {
          status: 'completed',
          completedAt: new Date(),
        });
        applyCardResponse(card);
      } catch (error) {
        logError('Failed to mark card as completed:', error);
        throw error;
      }
    },
    [applyCardResponse]
  );

  const markCardAsIncomplete = useCallback(
    async (cardId: string) => {
      try {
        const card = await updateCardApi(cardId, {
          status: 'in_review',
          completedAt: null,
        });
        applyCardResponse(card);
      } catch (error) {
        logError('Failed to mark card as incomplete:', error);
        throw error;
      }
    },
    [applyCardResponse]
  );

  const isCardCompleted = useCallback(
    (cardId: string): boolean => {
      return notesData[cardId]?.status === 'completed';
    },
    [notesData]
  );

  const markCardForRefetch = useCallback(
    async (cardId: string) => {
      try {
        const card = await updateCardApi(cardId, {
          shouldRefetch: true,
          refetchAt: new Date(),
        });
        applyCardResponse(card);
      } catch (error) {
        logError('Failed to mark card for refetch:', error);
        throw error;
      }
    },
    [applyCardResponse]
  );

  const markCardAsRefetched = useCallback(
    async (cardId: string) => {
      try {
        const card = await updateCardApi(cardId, {
          shouldRefetch: false,
          refetchAt: null,
        });
        applyCardResponse(card);
      } catch (error) {
        logError('Failed to clear refetch flag:', error);
        throw error;
      }
    },
    [applyCardResponse]
  );

  const archiveCard = useCallback(
    async (cardId: string) => {
      try {
        const card = await updateCardApi(cardId, { visibility: 'archived' });
        applyCardResponse(card);
      } catch (error) {
        logError('Failed to archive card:', error);
        throw error;
      }
    },
    [applyCardResponse]
  );

  const markCardAsDeleted = useCallback(
    async (cardId: string) => {
      try {
        const card = await updateCardApi(cardId, {
          status: 'deleted' as CardStatus,
          visibility: 'archived',
          shouldRefetch: false,
        });
        applyCardResponse(card);
      } catch (error) {
        logError('Failed to mark card as deleted:', error);
        throw error;
      }
    },
    [applyCardResponse]
  );

  const restoreCard = useCallback(
    async (cardId: string) => {
      try {
        const card = await updateCardApi(cardId, { visibility: 'visible' });
        applyCardResponse(card);
      } catch (error) {
        logError('Failed to restore card visibility:', error);
        throw error;
      }
    },
    [applyCardResponse]
  );

  const isCardArchived = useCallback(
    (cardId: string): boolean => notesData[cardId]?.visibility === 'archived',
    [notesData]
  );

  const shouldCardRefetch = useCallback(
    (cardId: string): boolean => {
      return notesData[cardId]?.shouldRefetch || false;
    },
    [notesData]
  );

  const getCardCode = useCallback(
    (cardId: string) => {
      const entry = notesData[cardId];
      return {
        overrideCode: entry?.codeOverride ?? null,
        reviewBlurb: entry?.codeReview ?? null,
        updatedAt: entry?.codeUpdatedAt,
      };
    },
    [notesData]
  );

  const saveCardCode = useCallback(
    async (
      cardId: string,
      overrideCode: string | null,
      reviewBlurb: string | null
    ) => {
      try {
        const card = await updateCardCode(cardId, {
          code: overrideCode,
          reviewBlurb,
        });
        applyCardResponse(card);
      } catch (error) {
        logError('Failed to save card code:', error);
        throw error;
      }
    },
    [applyCardResponse]
  );

  const upsertManualCardEntry = useCallback(
    async (cardId: string, payload: UpsertManualCardPayload) => {
      try {
        const card = await upsertManualCard(cardId, payload);
        applyCardResponse(card);
        return card;
      } catch (error) {
        logError('Failed to upsert manual card:', error);
        throw error;
      }
    },
    [applyCardResponse]
  );

  const value: CardNotesContextType = {
    notesData,
    editMode,
    addNote,
    editNote,
    deleteNote,
    setEditMode,
    clearEditMode,
    getCardNotes,
    getFieldEditedValue,
    setFieldEditedValue,
    resetFieldEdits,
    saveAllChanges,
    discardAllChanges,
    hasUnsavedChanges,
    markCardAsCompleted,
    markCardAsIncomplete,
    isCardCompleted,
    markCardForRefetch,
    markCardAsRefetched,
    shouldCardRefetch,
    getCardCode,
    saveCardCode,
    reloadCard: refreshCard,
    upsertManualCard: upsertManualCardEntry,
    archiveCard,
    markCardAsDeleted,
    restoreCard,
    isCardArchived,
  };

  return (
    <CardNotesContext.Provider value={value}>
      {children}
    </CardNotesContext.Provider>
  );
};

export const useCardNotes = (): CardNotesContextType => {
  const context = useContext(CardNotesContext);
  if (context === undefined) {
    throw new Error('useCardNotes must be used within a CardNotesProvider');
  }
  return context;
};
